import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import type { AdminSport, AdminSportsbook, AuditRow, SportDivision } from './api'
import { fetchAdminAudit } from './api'
import { formatRelative, formatTsUtc } from './time'

// Renders a recency-ordered diff log of every Distribution edit. The
// before/after columns are JSONB snapshots from the RPC; we summarize
// each delta into a short human line so the panel is scannable.
//
// Shapes the audit rows can carry, keyed by `table_name`:
//   sports              → { id, name, slug, states, internationals }
//   sportsbooks         → { id, name, status, states, allowed, blocked }
//   sport_sportsbooks   → either { sport_id, carriers: [{sportsbook_id, division_id}, …] }
//                         or     { sportsbook_id, carried: [{sport_id, division_id}, …] }
//   affiliate_links     → { sport_id, links: [{sportsbook_id, url}, …] }
//
// Resolving target names needs the catalog: AdminApp already fetches
// sports + sportsbooks + divisions and passes them down as props.

interface Props {
  sports: AdminSport[]
  sportsbooks: AdminSportsbook[]
  divisions: SportDivision[]
}

interface IntegrationAuditMarket {
  market_key: string
  market_label: string
  status: string
  needs_review: boolean
  review_note: string | null
}

interface JsonShape {
  // sports / sportsbooks editor shapes
  id?: number
  name?: string
  slug?: string | null
  states?: string[]
  internationals?: string[]
  allowed?: string[]
  blocked?: string[]
  // carriers RPC shapes (sport-side or sportsbook-side variant); traded_by
  // absent on audit rows written before migration 0053
  sport_id?: number
  sportsbook_id?: number
  carriers?: { sportsbook_id: number, division_id: number | null, traded_by?: string }[]
  carried?: { sport_id: number, division_id: number | null, traded_by?: string }[]
  // affiliate-links RPC shape; sport_trading_links (0057) reuses the key
  // with {group_name, label, url} elements — the render cases discriminate
  // by table_name and cast, never by element shape.
  links?: { sportsbook_id: number, url: string }[] | { group_name: string, label: string, url: string }[]
  // markets-traded RPC shape (string[]) — integration_markets (0070) reuses
  // the key with object elements; render cases discriminate by table_name.
  markets?: string[] | IntegrationAuditMarket[]
  // integrations / integration_markets RPC shapes (0070)
  key?: string
  integration_name?: string
  league_key?: string
  league_name?: string
  market_rows?: number
  leagues?: string[]
  // trading-notes RPC shape
  section?: string
  content?: string
  // trading-emails RPC shape
  to_emails?: string[]
  cc_emails?: string[]
  // absent on audit rows written before migration 0056
  bcc_emails?: string[]
  // sport_athletes row shape (name is shared with sports/sportsbooks above);
  // imported/skipped_existing come from the per-sport import-seed summary row.
  // is_active absent on audit rows written before migration 0055.
  imported?: number
  skipped_existing?: number
  birthdate?: string | null
  is_active?: boolean
}

function diffStringArray(before: string[] | undefined, after: string[] | undefined): { added: string[], removed: string[] } {
  const a = new Set(before ?? [])
  const b = new Set(after ?? [])
  const added: string[] = []
  const removed: string[] = []
  for (const v of b) if (!a.has(v)) added.push(v)
  for (const v of a) if (!b.has(v)) removed.push(v)
  return { added, removed }
}

function makeSummarizer(props: Props) {
  const sportsById = new Map(props.sports.map(s => [s.id, s.name]))
  const booksById = new Map(props.sportsbooks.map(b => [b.id, b.name]))
  const divisionsById = new Map(props.divisions.map(d => [d.id, d.name]))

  function sportLabel(id: number): string {
    return sportsById.get(id) ?? `sport #${id}`
  }
  function bookLabel(id: number): string {
    return booksById.get(id) ?? `book #${id}`
  }
  function carrierKey(c: { sportsbook_id: number, division_id: number | null }): string {
    return `${c.sportsbook_id}::${c.division_id ?? ''}`
  }
  function carriedKey(c: { sport_id: number, division_id: number | null }): string {
    return `${c.sport_id}::${c.division_id ?? ''}`
  }
  function carrierLabel(c: { sportsbook_id: number, division_id: number | null }): string {
    const div = c.division_id ? divisionsById.get(c.division_id) : null
    return div ? `${bookLabel(c.sportsbook_id)} (${div})` : bookLabel(c.sportsbook_id)
  }
  function carriedLabel(c: { sport_id: number, division_id: number | null }): string {
    const div = c.division_id ? divisionsById.get(c.division_id) : null
    return div ? `${sportLabel(c.sport_id)} (${div})` : sportLabel(c.sport_id)
  }
  // Pre-0053 audit rows have no traded_by — those pairs were backfilled
  // to 'us', so absent reads as ASD.
  function tradedTag(t?: string): string {
    return t === 'book' ? 'BOOK' : 'ASD'
  }

  // Render helpers — wrap added / removed token lists in colored spans
  // so a quick visual scan can separate "what got turned on" from "what
  // got turned off". Modifications (URL edits, name renames) stay neutral
  // since they're transitions, not pure adds or removes.
  function addSpan(text: string): ReactNode {
    return <span className="ad-audit-add">+{text}</span>
  }
  function remSpan(text: string): ReactNode {
    return <span className="ad-audit-rem">−{text}</span>
  }
  function addRemLine(label: string, added: string[], removed: string[], sep = ','): ReactNode | null {
    if (added.length === 0 && removed.length === 0) return null
    return (
      <>
        {label}:{' '}
        {added.length > 0 && addSpan(added.join(sep))}
        {added.length > 0 && removed.length > 0 && ' '}
        {removed.length > 0 && remSpan(removed.join(sep))}
      </>
    )
  }

  // Returns { target, lines } where target is the human-readable subject
  // (sport name, sportsbook name, etc.) and lines is the bullet-list
  // summary of what changed.
  return function summarize(row: AuditRow): { target: string, lines: ReactNode[] } {
    const before = (row.before ?? {}) as JsonShape
    const after = (row.after ?? {}) as JsonShape
    const lines: ReactNode[] = []

    if (row.table_name === 'sports' || row.table_name === 'sportsbooks') {
      const target = after.name ?? before.name ?? `${row.table_name} #${after.id ?? before.id ?? '?'}`

      function describe(label: string, key: keyof JsonShape) {
        const a = before[key]
        const b = after[key]
        if (!Array.isArray(a) && !Array.isArray(b)) return
        const { added, removed } = diffStringArray(a as string[] | undefined, b as string[] | undefined)
        const line = addRemLine(label, added, removed)
        if (line) lines.push(line)
      }
      // name + slug renames worth surfacing on the sports table. The slug
      // check uses == null on both sides so null → "" and "" → null don't
      // produce noisy diffs.
      if (before.name !== undefined && after.name !== undefined && before.name !== after.name) {
        lines.push(<>name: "{before.name}" → "{after.name}"</>)
      }
      const beforeSlug = before.slug ?? ''
      const afterSlug = after.slug ?? ''
      if (beforeSlug !== afterSlug) {
        lines.push(<>slug: "{beforeSlug}" → "{afterSlug}"</>)
      }
      describe('states', 'states')
      describe('intl', 'internationals')
      describe('allowed', 'allowed')
      describe('blocked', 'blocked')
      return { target, lines }
    }

    if (row.table_name === 'sport_sportsbooks') {
      // Sport-side: row carries sport_id + carriers. Sportsbook-side: row
      // carries sportsbook_id + carried. Detect via shape.
      const sportSide = before.sport_id !== undefined || after.sport_id !== undefined
      if (sportSide) {
        const sportId = (after.sport_id ?? before.sport_id) as number
        const target = sportLabel(sportId)
        const beforeMap = new Map((before.carriers ?? []).map(c => [carrierKey(c), c]))
        const afterMap = new Map((after.carriers ?? []).map(c => [carrierKey(c), c]))
        const added: string[] = []
        const removed: string[] = []
        const retraded: string[] = []
        for (const [k, c] of afterMap) {
          const prev = beforeMap.get(k)
          if (!prev) added.push(`${carrierLabel(c)} [${tradedTag(c.traded_by)}]`)
          else if (tradedTag(prev.traded_by) !== tradedTag(c.traded_by)) retraded.push(`${carrierLabel(c)}: ${tradedTag(prev.traded_by)} → ${tradedTag(c.traded_by)}`)
        }
        for (const [k, c] of beforeMap) if (!afterMap.has(k)) removed.push(carrierLabel(c))
        const line = addRemLine('carriers', added, removed, ', ')
        if (line) lines.push(line)
        if (retraded.length > 0) lines.push(<>carriers ~{retraded.join(', ')}</>)
        return { target, lines }
      }
      const bookId = (after.sportsbook_id ?? before.sportsbook_id) as number
      const target = bookLabel(bookId)
      const beforeMap = new Map((before.carried ?? []).map(c => [carriedKey(c), c]))
      const afterMap = new Map((after.carried ?? []).map(c => [carriedKey(c), c]))
      const added: string[] = []
      const removed: string[] = []
      const retraded: string[] = []
      for (const [k, c] of afterMap) {
        const prev = beforeMap.get(k)
        if (!prev) added.push(`${carriedLabel(c)} [${tradedTag(c.traded_by)}]`)
        else if (tradedTag(prev.traded_by) !== tradedTag(c.traded_by)) retraded.push(`${carriedLabel(c)}: ${tradedTag(prev.traded_by)} → ${tradedTag(c.traded_by)}`)
      }
      for (const [k, c] of beforeMap) if (!afterMap.has(k)) removed.push(carriedLabel(c))
      const line = addRemLine('carried', added, removed, ', ')
      if (line) lines.push(line)
      if (retraded.length > 0) lines.push(<>carried ~{retraded.join(', ')}</>)
      return { target, lines }
    }

    if (row.table_name === 'affiliate_links') {
      const sportId = (after.sport_id ?? before.sport_id) as number
      const target = sportLabel(sportId)
      const beforeMap = new Map(((before.links ?? []) as { sportsbook_id: number, url: string }[]).map(l => [l.sportsbook_id, l.url]))
      const afterMap = new Map(((after.links ?? []) as { sportsbook_id: number, url: string }[]).map(l => [l.sportsbook_id, l.url]))
      const added: string[] = []
      const removed: string[] = []
      const changed: string[] = []
      for (const [id, url] of afterMap) {
        if (!beforeMap.has(id)) {
          added.push(`${bookLabel(id)} → ${truncate(url)}`)
        }
        else if (beforeMap.get(id) !== url) {
          // Show old → new for URL changes; the prior value is the audit-
          // worthy bit ("what was this changed away from?").
          const oldUrl = beforeMap.get(id) ?? ''
          changed.push(`${bookLabel(id)}: ${truncate(oldUrl)} → ${truncate(url)}`)
        }
      }
      for (const [id] of beforeMap) {
        if (!afterMap.has(id)) removed.push(bookLabel(id))
      }
      if (added.length > 0) lines.push(<>links {addSpan(added.join(', '))}</>)
      if (changed.length > 0) lines.push(<>links ~{changed.join(', ')}</>)
      if (removed.length > 0) lines.push(<>links {remSpan(removed.join(', '))}</>)
      return { target, lines }
    }

    if (row.table_name === 'sport_markets_traded') {
      const sportId = (after.sport_id ?? before.sport_id) as number
      const target = sportLabel(sportId)
      const { added, removed } = diffStringArray(before.markets as string[] | undefined, after.markets as string[] | undefined)
      const line = addRemLine('markets', added, removed, ', ')
      if (line) lines.push(line)
      return { target, lines }
    }

    if (row.table_name === 'sport_trading_notes') {
      const sportId = (after.sport_id ?? before.sport_id) as number
      const target = sportLabel(sportId)
      const section = (after.section ?? before.section ?? '?') as string
      // Content blobs are long-form text — show truncated before → after
      // rather than a word diff. Empty side renders as ∅.
      const from = String(before.content ?? '').trim()
      const to = String(after.content ?? '').trim()
      const excerpt = (s: string) => (s === '' ? '∅' : `"${truncate(s.replace(/\s+/g, ' '), 80)}"`)
      lines.push(
        <>
          {section}
          :
          {' '}
          {excerpt(from)}
          {' → '}
          {excerpt(to)}
        </>,
      )
      return { target, lines }
    }

    if (row.table_name === 'sport_trading_emails') {
      const sportId = (after.sport_id ?? before.sport_id) as number
      const target = sportLabel(sportId)
      const toDiff = diffStringArray(before.to_emails, after.to_emails)
      const toLine = addRemLine('to', toDiff.added, toDiff.removed, ', ')
      if (toLine) lines.push(toLine)
      const ccDiff = diffStringArray(before.cc_emails, after.cc_emails)
      const ccLine = addRemLine('cc', ccDiff.added, ccDiff.removed, ', ')
      if (ccLine) lines.push(ccLine)
      const bccDiff = diffStringArray(before.bcc_emails, after.bcc_emails)
      const bccLine = addRemLine('bcc', bccDiff.added, bccDiff.removed, ', ')
      if (bccLine) lines.push(bccLine)
      return { target, lines }
    }

    if (row.table_name === 'sport_trading_links') {
      const sportId = (after.sport_id ?? before.sport_id) as number
      const target = sportLabel(sportId)
      type LinkRow = { group_name: string, label: string, url: string }
      const key = (l: LinkRow) => `${l.group_name}|${l.label}|${l.url}`
      const pretty = (l: LinkRow) => (l.group_name ? `${l.group_name} / ${l.label}` : l.label)
      const beforeRows = (before.links ?? []) as LinkRow[]
      const afterRows = (after.links ?? []) as LinkRow[]
      const beforeKeys = new Set(beforeRows.map(key))
      const afterKeys = new Set(afterRows.map(key))
      const added = afterRows.filter(l => !beforeKeys.has(key(l))).map(pretty)
      const removed = beforeRows.filter(l => !afterKeys.has(key(l))).map(pretty)
      const line = addRemLine('links', added, removed, ', ')
      if (line) lines.push(line)
      // Pure reorders diff to nothing — say so rather than rendering blank.
      if (!line) lines.push(<>links reordered</>)
      return { target, lines }
    }

    if (row.table_name === 'trading_pages') {
      // Global content pages (only 'sparket' today) — no sport target; the
      // page slug is the subject. Same excerpt treatment as trading notes.
      const target = (after.slug ?? before.slug ?? 'trading page') as string
      const from = String(before.content ?? '').trim()
      const to = String(after.content ?? '').trim()
      const excerpt = (s: string) => (s === '' ? '∅' : `"${truncate(s.replace(/\s+/g, ' '), 80)}"`)
      lines.push(
        <>
          content:
          {' '}
          {excerpt(from)}
          {' → '}
          {excerpt(to)}
        </>,
      )
      return { target, lines }
    }

    if (row.table_name === 'sport_athletes') {
      // Row-per-athlete audit entries: insert = added, delete = removed,
      // update = old → new (covers renames and birthdate corrections).
      const sportId = (after.sport_id ?? before.sport_id) as number
      const target = sportLabel(sportId)
      // The import seed writes one per-sport summary row instead of a
      // per-athlete delta: after = { sport_id, imported, skipped_existing }.
      if (after.imported !== undefined) {
        lines.push(
          <>
            imported
            {' '}
            {addSpan(String(after.imported))}
            , skipped
            {' '}
            {after.skipped_existing ?? 0}
            {' '}
            existing
          </>,
        )
        return { target, lines }
      }
      const label = (s: JsonShape) => (s.name ? (s.birthdate ? `${s.name} (${s.birthdate})` : s.name) : null)
      const from = label(before)
      const to = label(after)
      if (to && !from) lines.push(<>athlete {addSpan(to)}</>)
      else if (from && !to) lines.push(<>athlete {remSpan(from)}</>)
      else if (from && to && from !== to) lines.push(<>athlete: {from} → {to}</>)
      // Active-roster flag flips (0055) — often the ONLY delta on a row,
      // since the ●/○ toggle resends name/birthdate unchanged.
      if (from && to && before.is_active !== after.is_active) {
        lines.push(
          after.is_active
            ? <>athlete {after.name}: {addSpan('active')}</>
            : <>athlete {after.name}: {remSpan('inactive')}</>,
        )
      }
      return { target, lines }
    }

    if (row.table_name === 'integrations') {
      const target = `integration ${after.name ?? before.name ?? after.key ?? before.key ?? '?'}`
      if (row.action === 'create') lines.push(<>{addSpan('created')} · key {after.key}</>)
      else if (row.action === 'delete') {
        lines.push(<>{remSpan('deleted')} · {before.market_rows ?? 0} market rows · {(before.leagues ?? []).join(', ') || 'no leagues'}</>)
      }
      else if (before.name !== after.name) lines.push(<>name {before.name} → {after.name}</>)
      return { target, lines }
    }

    if (row.table_name === 'integration_markets') {
      const integ = after.integration_name ?? before.integration_name ?? '?'
      const league = after.league_name ?? before.league_name ?? after.league_key ?? before.league_key ?? '?'
      const target = `${integ} · ${league}`
      const b = (before.markets ?? []) as IntegrationAuditMarket[]
      const a = (after.markets ?? []) as IntegrationAuditMarket[]
      const byKey = (xs: IntegrationAuditMarket[]) => new Map(xs.map(m => [m.market_key, m]))
      const bm = byKey(b)
      const am = byKey(a)
      const added = a.filter(m => !bm.has(m.market_key)).map(m => `${m.market_label} (${m.status})`)
      const removed = b.filter(m => !am.has(m.market_key)).map(m => m.market_label)
      const changed: string[] = []
      for (const m of a) {
        const prev = bm.get(m.market_key)
        if (!prev) continue
        if (prev.status !== m.status) changed.push(`${m.market_label} ${prev.status} → ${m.status}`)
        if (prev.needs_review !== m.needs_review) changed.push(`${m.market_label} ${m.needs_review ? '⚠ flagged' : 'flag cleared'}`)
        else if ((prev.review_note ?? '') !== (m.review_note ?? '')) changed.push(`${m.market_label} note edited`)
        if (prev.market_label !== m.market_label) changed.push(`${prev.market_label} → ${m.market_label}`)
      }
      if (row.action === 'create') lines.push(<>{addSpan('listed')} · {a.length} market{a.length === 1 ? '' : 's'}</>)
      if (row.action === 'delete') lines.push(<>{remSpan('removed from integration')} · {b.length} market{b.length === 1 ? '' : 's'}</>)
      if (before.league_name !== undefined && after.league_name !== undefined && before.league_name !== after.league_name)
        lines.push(<>league name {before.league_name} → {after.league_name}</>)
      if (before.sport_id !== undefined && after.sport_id !== undefined && before.sport_id !== after.sport_id)
        lines.push(<>catalog sport {before.sport_id == null ? 'none' : sportLabel(before.sport_id)} → {after.sport_id == null ? 'none' : sportLabel(after.sport_id)}</>)
      if (added.length > 0) lines.push(<>markets {addSpan(added.join(', '))}</>)
      if (changed.length > 0) lines.push(<>markets ~{changed.join(', ')}</>)
      if (removed.length > 0) lines.push(<>markets {remSpan(removed.join(', '))}</>)
      return { target, lines }
    }

    // Unrecognized table — fall back to a name field if any RPC happens
    // to write one. Drops the literal '?' fallback.
    const target = after.name ?? before.name ?? row.table_name
    return { target, lines }
  }
}

function truncate(url: string, max = 60): string {
  return url.length > max ? `${url.slice(0, max)}…` : url
}

// Backing values for the table filter dropdown. `table_name` values come
// from the five RPCs in db/postgres/migrations/0007/0013/0016.
const TABLE_OPTIONS: { value: string, label: string }[] = [
  { value: '', label: 'All tables' },
  { value: 'sports', label: 'Sports' },
  { value: 'sportsbooks', label: 'Sportsbooks' },
  { value: 'sport_sportsbooks', label: 'Carriers' },
  { value: 'affiliate_links', label: 'Affiliate links' },
  { value: 'sport_markets_traded', label: 'Markets traded' },
  { value: 'sport_trading_notes', label: 'Trading notes' },
  { value: 'sport_trading_emails', label: 'Trading emails' },
  { value: 'sport_trading_links', label: 'Trading links' },
  { value: 'trading_pages', label: 'Trading pages' },
  { value: 'sport_athletes', label: 'Athletes' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'integration_markets', label: 'Integration markets' },
]

const PAGE_SIZE = 50

export default function AuditLog({ sports, sportsbooks, divisions }: Props) {
  const [rows, setRows] = useState<AuditRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // hasMore is "the last fetch returned a full page" — when a page comes
  // back short we know there's nothing more to load for the current filter.
  const [hasMore, setHasMore] = useState(true)

  // Filter inputs. The actor input debounces so we don't fire a request on
  // every keystroke; the table dropdown applies immediately.
  const [actorInput, setActorInput] = useState('')
  const [actorFilter, setActorFilter] = useState('')
  const [tableFilter, setTableFilter] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setActorFilter(actorInput), 300)
    return () => clearTimeout(id)
  }, [actorInput])

  // Reload from the top whenever filters change (or the user hits refresh).
  // Bumping `reloadKey` is the trigger; the load-more handler does not bump
  // it, it just appends.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchAdminAudit({
      limit: PAGE_SIZE,
      actor: actorFilter,
      tableName: tableFilter,
    })
      .then((data) => {
        if (cancelled) return
        setRows(data)
        setHasMore(data.length === PAGE_SIZE)
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [actorFilter, tableFilter, reloadKey])

  async function loadMore() {
    if (!rows || rows.length === 0 || loading || !hasMore) return
    const last = rows[rows.length - 1]
    if (!last) return
    const cursor = last.id
    setLoading(true)
    try {
      const next = await fetchAdminAudit({
        limit: PAGE_SIZE,
        beforeId: cursor,
        actor: actorFilter,
        tableName: tableFilter,
      })
      setRows(prev => [...(prev ?? []), ...next])
      setHasMore(next.length === PAGE_SIZE)
    }
    catch (e) {
      setError((e as Error).message)
    }
    finally {
      setLoading(false)
    }
  }

  const summarize = makeSummarizer({ sports, sportsbooks, divisions })

  return (
    <div className="ad-audit">
      <div className="ad-audit-head">
        <span className="b-mono">
          AUDIT LOG — {rows?.length ?? '—'} {hasMore ? 'LOADED' : 'ENTRIES'}
        </span>
        <button
          type="button"
          onClick={() => setReloadKey(k => k + 1)}
          className="b-btn-ghost b-btn-tiny"
          disabled={loading}
        >
          REFRESH
        </button>
      </div>

      <div className="ad-audit-filters">
        <div className="ad-audit-filter-input">
          <span className="b-mono-dim">/</span>
          <input
            value={actorInput}
            onChange={e => setActorInput(e.target.value)}
            placeholder="filter by actor (email)…"
            className="ad-list-search-input"
            spellCheck={false}
          />
          {actorInput && (
            <button
              type="button"
              onClick={() => setActorInput('')}
              className="ad-list-search-clear"
              aria-label="Clear actor filter"
            >
              ✕
            </button>
          )}
        </div>
        <select
          value={tableFilter}
          onChange={e => setTableFilter(e.target.value)}
          className="ad-audit-filter-select"
        >
          {TABLE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <p className="b-mono" style={{ color: '#dc2626' }}>
          ERR / audit fetch failed: {error}
        </p>
      )}

      {rows === null
        ? <p className="b-mono-dim">LOADING…</p>
        : rows.length === 0
          ? <p className="b-mono-dim">No matching entries.</p>
          : (
              <ul className="ad-audit-list">
                {rows.map((r) => {
                  const { target, lines } = summarize(r)
                  return (
                    <li key={r.id} className="ad-audit-item">
                      <div className="ad-audit-meta">
                        <span className="b-mono" title={formatTsUtc(r.ts)}>{formatRelative(r.ts)}</span>
                        <span className="b-mono-dim">·</span>
                        <span className="b-mono">{r.actor ?? 'unknown'}</span>
                        <span className="b-mono-dim">·</span>
                        <span className="b-mono-dim">{r.action} {r.table_name}</span>
                      </div>
                      <div className="ad-audit-target b-mono">{target}</div>
                      {lines.length > 0
                        ? (
                            <ul className="ad-audit-changes">
                              {lines.map((line, i) => (
                                <li key={i} className="b-mono ad-audit-change">{line}</li>
                              ))}
                            </ul>
                          )
                        : (
                            <p className="b-mono-dim ad-audit-change">no field deltas</p>
                          )}
                    </li>
                  )
                })}
              </ul>
            )}

      {rows && rows.length > 0 && (
        <div className="ad-audit-footer">
          {hasMore
            ? (
                <button
                  type="button"
                  onClick={loadMore}
                  className="b-btn-ghost b-btn-tiny"
                  disabled={loading}
                >
                  {loading ? 'LOADING…' : 'LOAD MORE'}
                </button>
              )
            : (
                <span className="b-mono-dim">end of log</span>
              )}
        </div>
      )}
    </div>
  )
}
